import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useLang, STR, loc } from "@/i18n/translations";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

const CATS = [
  { key: "all", fr: "Tous", en: "All" },
  { key: "artisanat", fr: "Artisanat", en: "Craft" },
  { key: "mode", fr: "Mode & accessoires", en: "Fashion" },
  { key: "culturel", fr: "Produits culturels", en: "Cultural" },
  { key: "merch", fr: "Merch Sily", en: "Sily Merch" },
];

export default function Shop() {
  const { t, lang } = useLang();
  const { add, setOpen } = useCart();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const filtered = products.filter((p) => (cat === "all" ? true : p.category === cat));

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.nav.shop)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{t(STR.home.shopTitle)}</h1>
        <p className="text-cream/60 mt-5 text-lg max-w-xl">{t(STR.home.shopText)}</p>
        <div className="gold-line w-24 my-10" />

        <div className="flex flex-wrap gap-3 mb-10">
          {CATS.map((c) => (
            <button
              key={c.key} onClick={() => setCat(c.key)} data-testid={`shop-cat-${c.key}`}
              className={`px-5 py-2 rounded-full text-sm border transition-colors ${
                cat === c.key ? "bg-gold text-obsidian border-gold" : "border-gold/25 text-cream/70 hover:border-gold"
              }`}
            >
              {lang === "fr" ? c.fr : c.en}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="card-dark rounded-2xl overflow-hidden group flex flex-col"
              data-testid={`shop-product-${p.id}`}
            >
              <div className="aspect-square overflow-hidden">
                <img src={p.image} alt={loc(p, "name", lang)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <p className="text-[10px] uppercase tracking-widest text-gold/70 mb-1">{(() => { const c = CATS.find((x) => x.key === p.category); return c ? c[lang] : p.category; })()}</p>
                <h3 className="font-serif text-xl">{loc(p, "name", lang)}</h3>
                <p className="text-cream/50 text-sm mt-1 flex-1">{loc(p, "description", lang)}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-gold text-lg">{p.price.toFixed(2)} €</span>
                  <button onClick={() => { add(p); toast.success(t({ fr: "Ajouté au panier", en: "Added to cart" })); }} data-testid={`shop-add-${p.id}`} className="btn-gold rounded-full px-4 py-2 text-xs flex items-center gap-1.5">
                    <ShoppingBag className="w-3.5 h-3.5" /> {t(STR.common.addToCart)}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-cream/50 py-16 text-center">{t({ fr: "Aucun produit.", en: "No products." })}</p>}
      </div>
    </div>
  );
}
